'use client';

import { VERIFY_OTP_RESULT_STATUS } from '@algoacademy/backend/modules/phone-call-booking/endpoints/mutations/verify-one-time-password/output.dto';
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { showErrorToastWithContactFormLink } from '@algoacademy/design-system/web/feedback/Toaster/ToasterError';
import { ErrorMessage, Field, Fieldset } from '@algoacademy/design-system/web/forms/Fieldset';
import { FadeUp } from '@algoacademy/design-system/web/marketing/animation/FadeUp';
import { P1 } from '@algoacademy/design-system/web/marketing/typography/Paragraph';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTRPC } from '@/app/_trpc/config';
import type { CalendarEvent } from '../../../_components/CalendarEvent';
import type { ClientData } from '../../../page';
import { type CodeVerificationSchema, codeVerificationSchemaZ } from './schema';
type CodeVerificationSectionProps = {
  activeStepIdx: number;
  setActiveStepIdx: (idx: number) => void;
  clientData: ClientData;
  selectedEvent: CalendarEvent | null;
};
export function CodeVerificationForm({
  activeStepIdx,
  setActiveStepIdx,
  clientData,
  selectedEvent
}: CodeVerificationSectionProps) {
  const {
    register,
    handleSubmit
  } = useForm<CodeVerificationSchema>({
    resolver: zodResolver(codeVerificationSchemaZ)
  });
  const [values, setValues] = useState<string[]>(['', '', '', '', '', '']);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const trpc = useTRPC();
  const verifyOTPMutation = useMutation(trpc.phoneCallBooking.verifyOneTimePassword.mutationOptions());
  const createOTPMutation = useMutation(trpc.phoneCallBooking.createOneTimePassword.mutationOptions());
  const bookAPhoneCallMutation = useMutation(trpc.phoneCallBooking.bookAPhoneCall.mutationOptions());
  const sendSMSToClientMutation = useMutation(trpc.notifications.sendSMSToClient.mutationOptions());
  const sendSMSToAlgoMutation = useMutation(trpc.notifications.sendSMSToAlgo.mutationOptions());
  const onSubmit = async (data: CodeVerificationSchema) => {
    const verificationCode = Object.values(data).join('');
    try {
      const verifyOTPResult = await verifyOTPMutation.mutateAsync({
        otp: verificationCode,
        phoneNumber: clientData.phoneNumber
      });
      switch (verifyOTPResult.status as VERIFY_OTP_RESULT_STATUS) {
        case VERIFY_OTP_RESULT_STATUS.SUCCESS:
          {
            try {
              await bookAPhoneCallMutation.mutateAsync({
                scheduleId: selectedEvent?.id ?? '',
                client: {
                  message: clientData.message ?? '',
                  phoneNumber: clientData.phoneNumber ?? '',
                  fullname: clientData.fullname ?? ''
                }
              });
              await sendSMSToClientMutation.mutateAsync({
                phoneNumber: clientData.phoneNumber ?? '',
                message: `Mentor AlgoAcademy zadzwoni do Ciebie ${selectedEvent?.startTime ? format(selectedEvent.startTime, 'dd.MM.yyyy') : ''} o ${selectedEvent?.startTime ? format(selectedEvent.startTime, 'HH:mm') : ''}. Jeżeli chcesz odwołać lub zmienić termin spotkania skorzystaj z linku: https://algoacademy.pl/bezplatna-konsultacja/odwolaj/${btoa(selectedEvent?.id ?? '')}`
              });
              await sendSMSToAlgoMutation.mutateAsync({
                message: `${clientData.fullname} umówił spotkanie z mentorem w terminie ${selectedEvent?.startTime ? format(selectedEvent.startTime, 'dd.MM.yyyy') : ''} o ${selectedEvent?.startTime ? format(selectedEvent.startTime, 'HH:mm') : ''}`
              });
            } catch {
              setErrorMessage('Wystąpił błąd podczas rezerwacji spotkania.');
              return;
            }
            setActiveStepIdx(activeStepIdx + 1);
            break;
          }
        case VERIFY_OTP_RESULT_STATUS.EXPIRED:
          {
            setErrorMessage('Kod wygasł. Wysłaliśmy SMS z nowym kodem.');
            createOTPMutation.mutate({
              phoneNumber: clientData.phoneNumber
            });
            break;
          }
        case VERIFY_OTP_RESULT_STATUS.WRONG_CODE:
          {
            setErrorMessage('Podaj poprawny kod weryfikacyjny.');
            break;
          }
        // case VERIFY_OTP_RESULT_STATUS.ALREADY_BOOKED_APPOINTMENT:
        //   setActiveStepIdx(activeStepIdx + 1)
        //   break
        default:
          {
            setErrorMessage('Wystąpił błąd podczas weryfikacji kodu.');
            showErrorToastWithContactFormLink();
          }
      }
    } catch {
      setErrorMessage('Wystąpił błąd podczas weryfikacji kodu.');
    }
  };
  const verificationCodeKeys = ['verificationCode_1', 'verificationCode_2', 'verificationCode_3', 'verificationCode_4', 'verificationCode_5', 'verificationCode_6'] as const;
  return <FadeUp>
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-6 py-6 text-center'>
        <P1>Wysłaliśmy kod weryfikacyjny na Twój numer telefonu.</P1>

        <Fieldset className='flex flex-row justify-center gap-2 space-y-12'>
          {verificationCodeKeys.map((fieldName, idx) => <Field key={fieldName}>
              <input className='w-10 rounded-lg border bg-white p-2 text-center sm:w-10' type='text' maxLength={1} {...register(fieldName, {
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value.replaceAll(/\D/g, '');
              const newValues = [...values];
              if (value.length === 1) {
                newValues[idx] = value;
                setValues(newValues);
                if (idx < values.length - 1) {
                  inputRefs.current[idx + 1]?.focus();
                }
              } else if (value.length === 0) {
                newValues[idx] = '';
                setValues(newValues);
              }
            }
          })} ref={(el: HTMLInputElement | null) => {
            inputRefs.current[idx] = el;
            const {
              ref
            } = register(fieldName);
            if (typeof ref === 'function') {
              ref(el);
            }
          }} onKeyDown={(e_0: React.KeyboardEvent<HTMLInputElement>) => {
            if (e_0.key === 'Backspace' && values[idx] === '') {
              if (idx > 0) {
                inputRefs.current[idx - 1]?.focus();
                const newValues_0 = [...values];
                newValues_0[idx - 1] = '';
                setValues(newValues_0);
              }
            } else if (e_0.key === 'Backspace') {
              const newValues_1 = [...values];
              newValues_1[idx] = '';
              setValues(newValues_1);
            }
          }} onFocus={() => {
            inputRefs.current[idx]?.select();
          }} />
            </Field>)}
        </Fieldset>

        <Field>
          <ErrorMessage>{errorMessage}</ErrorMessage>
        </Field>

        <div className='flex flex-row justify-end gap-3'>
          <Button outline size='xl' onClick={() => setActiveStepIdx(activeStepIdx - 1)}>
            Powrót
          </Button>
          <Button size='xl' type='submit' disabled={values.includes('')}>
            Przejdź dalej
          </Button>
        </div>
      </form>
    </FadeUp>;
}